import React, { useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../utils';
import type { Toast as ToastType } from '../../../lib/toast.types';

interface ToastProps {
    toast: ToastType;
    onRemove: (id: string) => void;
    index: number;
}

const toastConfig = {
    success: {
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ),
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
        border: 'border-emerald-200 dark:border-emerald-500/20',
        text: 'text-emerald-800 dark:text-emerald-200',
        iconBg: 'bg-emerald-100 dark:bg-emerald-500/20',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
    },
    error: {
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-500/20',
        text: 'text-red-800 dark:text-red-200',
        iconBg: 'bg-red-100 dark:bg-red-500/20',
        iconColor: 'text-red-600 dark:text-red-400',
    },
    warning: {
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
            </svg>
        ),
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-500/20',
        text: 'text-amber-800 dark:text-amber-200',
        iconBg: 'bg-amber-100 dark:bg-amber-500/20',
        iconColor: 'text-amber-600 dark:text-amber-400',
    },
    info: {
        icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
        ),
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-500/20',
        text: 'text-blue-800 dark:text-blue-200',
        iconBg: 'bg-blue-100 dark:bg-blue-500/20',
        iconColor: 'text-blue-600 dark:text-blue-400',
    },
};

export const Toast: React.FC<ToastProps> = ({ toast, onRemove, index }) => {
    const config = toastConfig[toast.type];
    const duration = toast.duration ?? 3000;

    const handleRemove = useCallback(() => {
        onRemove(toast.id);
    }, [onRemove, toast.id]);

    useEffect(() => {
        if (duration === Infinity) return;

        const timer = setTimeout(() => {
            handleRemove();
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, handleRemove]);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: -20, scale: 0.95, x: 20 }}
            animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
            exit={{ opacity: 0, x: 20, scale: 0.95 }}
            transition={{ 
                type: 'spring',
                stiffness: 400,
                damping: 30,
                delay: index * 0.05
            }}
            className={cn(
                'relative flex items-start gap-3 p-4 rounded-xl min-w-[280px] max-w-[360px]',
                'border shadow-lg backdrop-blur-sm',
                'pointer-events-auto cursor-default',
                config.bg,
                config.border
            )}
            role="alert"
            aria-live="polite"
        >
            <div className={cn(
                'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
                config.iconBg,
                config.iconColor
            )}>
                {config.icon}
            </div>

            <div className="flex-1 min-w-0 pt-0.5">
                <p className={cn('text-sm font-medium', config.text)}>
                    {toast.message}
                </p>
                
                {toast.action && (
                    <button
                        onClick={() => {
                            toast.action?.onClick();
                            handleRemove();
                        }}
                        className={cn(
                            'mt-2 text-xs font-medium underline-offset-2 hover:underline',
                            config.text
                        )}
                    >
                        {toast.action.label}
                    </button>
                )}
            </div>

            <button
                onClick={handleRemove}
                className={cn(
                    'flex-shrink-0 p-1 rounded-md transition-colors',
                    'hover:bg-black/5 dark:hover:bg-white/10',
                    config.text
                )}
                aria-label="Закрыть уведомление"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            </button>

            {duration !== Infinity && (
                <motion.div
                    initial={{ scaleX: 1 }}
                    animate={{ scaleX: 0 }}
                    transition={{ duration: duration / 1000, ease: 'linear' }}
                    className={cn(
                        'absolute bottom-0 left-0 right-0 h-0.5 origin-left rounded-full',
                        config.iconColor.replace('text-', 'bg-')
                    )}
                />
            )}
            </motion.div>
    );
}